import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Network, RefreshCw, Route, Wallet } from "lucide-react";
import RoutingPanel from "./RoutingPanel";
import VendorCard from "./VendorCard";
import { inr } from "./format";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

function Headline({ label, value, sub, accent = "slate", testid }) {
  const tone =
    accent === "amber" ? "text-amber-700" : accent === "emerald" ? "text-emerald-700" : "text-slate-900";
  return (
    <div className="rounded-xl border border-slate-200/80 bg-white px-4 py-3" data-testid={testid}>
      <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">{label}</div>
      <div className={`text-2xl font-bold leading-tight ${tone}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

/** CRM → Dashboard → Reports: every paid data vendor on one board. */
export default function ProviderBoard() {
  const [board, setBoard] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const res = await axios.get(`${API_URL}/dashboard/services/providers/board`);
      setBoard(res.data);
    } catch (e) {
      if (e.response?.status === 403) setBoard({ forbidden: true });
      else toast.error(e.response?.data?.detail || "Could not load the provider board");
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (board?.forbidden) {
    return (
      <div
        className="bg-white rounded-xl border border-slate-200/80 p-10 text-center text-sm text-slate-500"
        data-testid="rep-forbidden"
      >
        These reports are restricted to the CEO.
      </div>
    );
  }

  if (!board) {
    return (
      <div className="bg-white rounded-xl border border-slate-200/80 p-10 text-center text-sm text-slate-400">
        Loading the provider board…
      </div>
    );
  }

  const labels = Object.fromEntries(board.vendors.map((v) => [v.id, v.label]));
  const chainOf = (id) => board.capabilities.find((c) => c.id === id);
  const challan = chainOf("challan");
  const rc = chainOf("rc");
  const lowCount = board.summary.low.length;

  return (
    <div className="space-y-4" data-testid="provider-board">
      <div className="flex items-center gap-2 flex-wrap">
        <Network className="w-4 h-4 text-emerald-600" />
        <h3 className="text-sm font-semibold text-slate-700">Data vendors</h3>
        <button
          onClick={load}
          disabled={busy}
          className="ml-auto p-1.5 rounded-lg text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 disabled:opacity-50"
          title="Refresh"
          data-testid="board-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Headline
          label="Outstanding balance"
          value={`₹${inr(board.summary.outstanding_inr)}`}
          sub={lowCount ? `${lowCount} wallet under the floor` : "across prepaid wallets"}
          accent={lowCount ? "amber" : "slate"}
          testid="board-outstanding"
        />
        <Headline
          label="Spend · 30 days"
          value={`₹${inr(board.summary.spend_30d_inr)}`}
          sub="counted from the call log"
          testid="board-spend"
        />
        <Headline
          label="Challans answered by"
          value={labels[challan?.order?.[0]] || "—"}
          sub={challan?.order?.map((p) => labels[p] || p).join(" → ")}
          accent="emerald"
          testid="board-challan-primary"
        />
        <Headline
          label="RC answered by"
          value={labels[rc?.order?.[0]] || "—"}
          sub={rc?.order?.map((p) => labels[p] || p).join(" → ")}
          accent="emerald"
          testid="board-rc-primary"
        />
      </div>

      <div className="bg-slate-50/80 rounded-xl border border-slate-200/80 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Route className="w-4 h-4 text-slate-500" />
          <h4 className="text-sm font-semibold text-slate-700">Who answers first</h4>
          <span className="text-[11px] text-slate-400">
            reorder, drop or add a vendor — live on the next lookup, no deploy
          </span>
        </div>
        <div className="grid lg:grid-cols-2 gap-3">
          {board.capabilities.map((capability) => (
            <RoutingPanel
              key={capability.id}
              capability={capability}
              vendorLabels={labels}
              onSaved={load}
            />
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Wallet className="w-4 h-4 text-slate-500" />
        <h4 className="text-sm font-semibold text-slate-700">Vendors, APIs &amp; balances</h4>
        <span className="text-[11px] text-slate-400">click a vendor to open it</span>
        {board.summary.untracked.length > 0 && (
          <span className="text-[11px] text-slate-400">
            {board.summary.untracked.length} post-paid vendor(s) with no balance to track
          </span>
        )}
      </div>
      <div className="space-y-2">
        {board.vendors.map((vendor) => (
          <VendorCard key={vendor.id} vendor={vendor} onReload={load} />
        ))}
      </div>
    </div>
  );
}
